import { PlanControlSlot } from '../upgrade/UpgradeExperience';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref } from 'firebase/database';
import {
  FiArrowLeft,
  FiArrowRight,
  FiBarChart2,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiRefreshCw,
  FiSearch,
  FiTarget,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from 'react-icons/fi';
import { db } from '../../firebaseConfig';
import { ensureCareerPaths } from '../careerCoachApi';
import MarkdownResponse from '../components/MarkdownResponse';
import { getCachedFirebaseValue, subscribeCachedValue } from '../firebaseDataCache';
import { useLanguage } from '../contexts/LanguageContext';

const MODE_KEYS = ['promotion', 'parallel', 'pivoting'];

const TABS = MODE_KEYS.map((key) => ({ key }));

function toNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function normalizeArray(value) {
  return Array.isArray(value) ? value.filter(Boolean).map((item) => String(item)) : [];
}

function normalizePayRange(value) {
  const raw = value && typeof value === 'object' ? value : {};
  return {
    low: toNumber(raw.low),
    median: toNumber(raw.median),
    high: toNumber(raw.high),
    confidence: raw.confidence || 'limited',
    source: raw.source || '',
    sourceUrl: raw.sourceUrl || '',
    occupationTitle: raw.occupationTitle || '',
  };
}

function normalizeHiringOutlook(value) {
  const raw = value && typeof value === 'object' ? value : {};
  return {
    label: raw.label || 'Limited data',
    percentChange: toNumber(raw.percentChange),
    annualOpenings: toNumber(raw.annualOpenings),
    confidence: raw.confidence || 'limited',
    source: raw.source || '',
    sourceUrl: raw.sourceUrl || '',
    occupationTitle: raw.occupationTitle || '',
    note: raw.note || '',
  };
}

function normalizePosition(position) {
  return {
    title: position?.title || 'Recommended path',
    summary: position?.summary || '',
    fitLabel: position?.fitLabel || 'Promising',
    effortLabel: position?.effortLabel || 'Medium',
    timeHorizon: position?.timeHorizon || '90 days',
    payRange: normalizePayRange(position?.payRange),
    hiringOutlook: normalizeHiringOutlook(position?.hiringOutlook),
    alreadyHave: normalizeArray(position?.alreadyHave),
    skillsToBuild: normalizeArray(position?.skillsToBuild),
    next30Days: normalizeArray(position?.next30Days),
    next90Days: normalizeArray(position?.next90Days),
    networkingTargets: normalizeArray(position?.networkingTargets),
    markdown: position?.markdown || '',
  };
}

function normalizeSection(section, fallbackTitle) {
  return {
    status: section?.status || 'generating',
    title: section?.title || fallbackTitle,
    sources: Array.isArray(section?.sources) ? section.sources : [],
    positions: Array.isArray(section?.positions) ? section.positions.map(normalizePosition) : [],
    error: section?.error || '',
  };
}

function confidenceLabel(value, translate) {
  if (value === 'official') return translate('official');
  if (value === 'estimated') return translate('estimate');
  return translate('liveCheck');
}

function openingsLabel(value, language, translate) {
  const number = toNumber(value);
  if (number === null) return '';
  return translate('openings').replace('{count}', new Intl.NumberFormat(language === 'ar' ? 'ar-LB' : 'en-US').format(Math.round(number)));
}

function hiringOutlookLabel(outlook, translate) {
  if (outlook.label && outlook.label !== 'Limited data') return outlook.label;
  if (outlook.percentChange !== null) {
    if (outlook.percentChange >= 5) return translate('growing');
    if (outlook.percentChange <= -2) return translate('limited');
    return translate('steady');
  }
  if (outlook.annualOpenings !== null) return translate('rolesExist');
  return translate('liveCheck');
}

function hiringOutlookDetail(outlook, language, translate) {
  const openingDetail = openingsLabel(outlook.annualOpenings, language, translate);
  const formattedPercent = outlook.percentChange !== null ? new Intl.NumberFormat(language === 'ar' ? 'ar-LB' : 'en-US').format(outlook.percentChange) : '';
  const facts = [
    outlook.percentChange !== null ? translate('projected').replace('{value}', `${outlook.percentChange > 0 ? '+' : ''}${formattedPercent}`) : '',
    openingDetail,
  ].filter(Boolean);
  if (facts.length) return facts.join(' | ');
  if (outlook.note && outlook.note !== 'No structured occupation data configured.') return outlook.note;
  return `${confidenceLabel(outlook.confidence, translate)}. ${translate('confirmPosts')}`;
}

function fitDisplayLabel(value, translate) {
  const key = String(value || 'Promising').toLowerCase();
  return ['strong', 'promising', 'stretch'].includes(key) ? translate(`fitLabels.${key}`) : value;
}

function effortDisplayLabel(value, translate) {
  const key = String(value || 'Medium').toLowerCase();
  return ['low', 'medium', 'high'].includes(key) ? translate(`effortLabels.${key}`) : value;
}

function positionTitle(position, translate) {
  return position.title === 'Recommended path' ? translate('recommendedPath') : position.title;
}

function timeHorizonLabel(position, translate) {
  return position.timeHorizon === '90 days' ? translate('ninetyDays') : position.timeHorizon;
}

function hasStructuredPositionContent(position) {
  const outlook = position.hiringOutlook || {};
  return Boolean(
    position.summary
    || position.alreadyHave.length
    || position.skillsToBuild.length
    || position.next30Days.length
    || position.next90Days.length
    || position.networkingTargets.length
    || outlook.percentChange !== null
    || outlook.annualOpenings !== null
    || (outlook.label && outlook.label !== 'Limited data')
    || outlook.note
    || outlook.source
  );
}

function markdownWithoutCallouts(content) {
  return String(content || '')
    .split('\n')
    .filter((line) => !line.trimStart().startsWith('>'))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function SourceStrip({ sources, translate }) {
  if (!sources.length) return null;
  return (
    <div className="js-career-paths__sources" aria-label={translate('sources')}>
      {sources.map((source, index) => (
        <a key={`${source.index || index}-${source.url}`} href={source.url} target="_blank" rel="noreferrer">
          <strong>[{source.index || index + 1}]</strong>
          <span>{source.title || source.domain || translate('source')}</span>
        </a>
      ))}
    </div>
  );
}

function IntentPanel({
  mode,
  specificRole,
  ensuring,
  onModeChange,
  onSpecificRoleChange,
  onSubmit,
  options,
  translate,
}) {
  return (
    <form className="js-career-intent" onSubmit={onSubmit}>
      <div className="js-career-intent__head">
        <h2>{translate('intentTitle')}</h2>
        <p>{translate('intentDescription')}</p>
      </div>

      <fieldset className="js-career-intent__choices" aria-label={translate('direction')}>
        {options.map((option) => (
          <label
            key={option.key}
            className={mode === option.key ? 'is-active' : ''}
          >
            <input
              type="radio"
              name="career-path-mode"
              value={option.key}
              checked={mode === option.key}
              onChange={() => onModeChange(option.key)}
            />
            <span className="js-career-intent__radio" aria-hidden="true" />
            <span className="js-career-intent__option-title">{option.prompt}</span>
            <span className="js-career-intent__option-desc">{option.description}</span>
          </label>
        ))}
      </fieldset>

      {mode === 'pivoting' && (
        <label className="js-career-intent__field">
          <span>{translate('pivotQuestion')}</span>
          <input
            type="text"
            value={specificRole}
            onChange={(event) => onSpecificRoleChange(event.target.value)}
            placeholder={translate('pivotPlaceholder')}
            maxLength={140}
            required
          />
        </label>
      )}

      <button type="submit" className="js-career-intent__submit" disabled={ensuring}>
        {ensuring ? <FiRefreshCw aria-hidden="true" /> : <FiSearch aria-hidden="true" />}
        <span>{ensuring ? translate('starting') : translate('showPaths')}</span>
      </button>
    </form>
  );
}

function OutlookLine({ outlook }) {
  const percent = toNumber(outlook.percentChange);
  if (percent === null) return null;
  const clamped = Math.max(-30, Math.min(30, percent));
  const endY = 18 - (clamped / 30) * 11;
  return (
    <svg className="js-career-outlook-line" viewBox="0 0 100 34" aria-hidden="true" focusable="false">
      <line x1="8" y1="18" x2="92" y2="18" />
      <path d={`M8 18 C32 18, 58 ${endY}, 92 ${endY}`} />
      <circle cx="92" cy={endY} r="3" />
    </svg>
  );
}

function Metric({ icon: Icon, label, value, detail, children }) {
  return (
    <div className="js-career-metric">
      <div className="js-career-metric__head">
        <Icon aria-hidden="true" />
        <span>{label}</span>
      </div>
      <strong>{value}</strong>
      {detail && <p>{detail}</p>}
      {children}
    </div>
  );
}

function DetailList({ title, items, icon: Icon, children, id }) {
  if (!items.length) return null;
  return (
    <div className="js-career-card__list" id={id} tabIndex={id ? -1 : undefined} aria-label={id ? title : undefined}>
      <h3>
        <Icon aria-hidden="true" />
        <span>{title}</span>
      </h3>
      {items.length > 0 && <ul>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>}
      {children}
    </div>
  );
}

function PositionCard({ position, onOpen, translate }) {
  const outlook = position.hiringOutlook;
  const hasStructuredContent = hasStructuredPositionContent(position);
  const outlookLabel = hiringOutlookLabel(outlook, translate);

  return (
    <article className="js-career-card">
      <button type="button" className="js-career-card__trigger" onClick={onOpen}>
        <span className="js-career-card__head">
          <span>
            <span className="js-career-card__time">{timeHorizonLabel(position, translate)}</span>
            <strong>{positionTitle(position, translate)}</strong>
            {position.summary && <em>{position.summary}</em>}
          </span>
          <span className="js-career-card__badges" aria-label={translate('pathSummary')}>
            {hasStructuredContent && (
              <>
                <span className={`is-fit-${position.fitLabel.toLowerCase()}`}>{translate('fit')}: {fitDisplayLabel(position.fitLabel, translate)}</span>
                <span>{translate('work')}: {effortDisplayLabel(position.effortLabel, translate)}</span>
                <span>{translate('outlook')}: {outlookLabel}</span>
              </>
            )}
            <FiChevronRight className="js-icon--directional" aria-hidden="true" />
          </span>
        </span>
      </button>
    </article>
  );
}

function PositionDetail({ position, language, translate, skillsAction }) {
  const sectionId = useId();
  const jumps = [
    ['skills', position.skillsToBuild, language === 'ar' ? 'المهارات' : 'Skills'],
    ['month', position.next30Days, language === 'ar' ? '٣٠ يوماً' : '30 days'],
    ['quarter', position.next90Days, language === 'ar' ? '٩٠ يوماً' : '90 days'],
  ].filter(([, items]) => items.length);
  const outlook = position.hiringOutlook;
  const hasStructuredContent = hasStructuredPositionContent(position);
  const hasDetails = Boolean(
    position.alreadyHave.length
    || position.skillsToBuild.length
    || position.next30Days.length
    || position.next90Days.length
    || position.networkingTargets.length
  );
  const legacyMarkdown = hasStructuredContent ? '' : markdownWithoutCallouts(position.markdown);
  const outlookLabel = hiringOutlookLabel(outlook, translate);
  const outlookDetail = hiringOutlookDetail(outlook, language, translate);

  return (
    <article className="js-career-detail">
      <header className="js-career-detail__head">
        <span>{timeHorizonLabel(position, translate)}</span>
        <h2>{positionTitle(position, translate)}</h2>
        {position.summary && <p>{position.summary}</p>}
        <div className="js-career-card__badges" aria-label={translate('pathRatings')}>
          {hasStructuredContent && (
            <>
              <span className={`is-fit-${position.fitLabel.toLowerCase()}`}>{translate('fit')}: {fitDisplayLabel(position.fitLabel, translate)}</span>
              <span>{translate('work')}: {effortDisplayLabel(position.effortLabel, translate)}</span>
              <span>{translate('outlook')}: {outlookLabel}</span>
            </>
          )}
        </div>
      </header>

      <div className="js-career-card__content">
        {hasStructuredContent ? (
          <>
            {jumps.length > 0 && <nav className="js-mobile-only js-career-jumps" aria-label={language === 'ar' ? 'انتقل إلى القسم' : 'Jump to section'}>
              {jumps.map(([key, , label]) => <button type="button" key={key} onClick={() => {
                const section = document.getElementById(`${sectionId}-${key}`);
                section?.scrollIntoView({ block: 'start' });
                section?.focus({ preventScroll: true });
              }}>{label}</button>)}
            </nav>}
            <div className="js-career-card__metrics">
              <Metric
                icon={FiTrendingUp}
                label={translate('hiringOutlook')}
                value={outlookLabel}
                detail={outlookDetail}
              >
                <OutlookLine outlook={outlook} />
              </Metric>
              <Metric
                icon={FiClock}
                label={translate('effort')}
                value={effortDisplayLabel(position.effortLabel, translate)}
                detail={position.fitLabel === 'Stretch' ? translate('moreProof') : translate('currentProof')}
              />
            </div>

            {hasDetails && (
              <div className="js-career-card__details">
                <DetailList title={translate('alreadyHave')} items={position.alreadyHave} icon={FiCheckCircle} />
                <DetailList id={`${sectionId}-skills`} title={translate('skillsToBuild')} items={position.skillsToBuild} icon={FiTarget}>{skillsAction}</DetailList>
                <DetailList id={`${sectionId}-month`} title={translate('next30')} items={position.next30Days} icon={FiBarChart2} />
                <DetailList id={`${sectionId}-quarter`} title={translate('next90')} items={position.next90Days} icon={FiClock} />
                <DetailList title={translate('people')} items={position.networkingTargets} icon={FiUsers} />
              </div>
            )}
          </>
        ) : legacyMarkdown && (
          <div className="js-career-card__markdown">
            <MarkdownResponse content={legacyMarkdown} />
          </div>
        )}
      </div>
    </article>
  );
}

function CareerPathsScreen({ user }) {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const tc = (key) => t(`careerPathsPage.${key}`);
  const modeOptions = MODE_KEYS.map((key) => ({ key, label: tc(`modes.${key}.label`), prompt: tc(`modes.${key}.prompt`), description: tc(`modes.${key}.description`) }));
  const careerPathsPath = user?.uid ? `people/${user.uid}/careerCoach/careerPaths` : '';
  const cachedCareerPaths = careerPathsPath ? getCachedFirebaseValue(careerPathsPath) : undefined;
  const initialRecord = cachedCareerPaths || null;
  const initialMode = TABS.some((tab) => tab.key === initialRecord?.intentMode)
    ? initialRecord.intentMode
    : 'promotion';
  const [record, setRecord] = useState(initialRecord);
  const [activeTab, setActiveTab] = useState(initialMode);
  const [intentMode, setIntentMode] = useState(initialMode);
  const [specificRole, setSpecificRole] = useState(initialRecord?.specificRole || '');
  const [hasRequestedPaths, setHasRequestedPaths] = useState(Boolean(initialRecord?.status));
  const [ensuring, setEnsuring] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [error, setError] = useState('');
  const [selectedPositionKey, setSelectedPositionKey] = useState('');
  const [recordLoaded, setRecordLoaded] = useState(!user?.uid || cachedCareerPaths !== undefined);
  const appliedRecordIntentRef = useRef(Boolean(initialRecord?.status));

  const sections = useMemo(() => {
    const rawSections = record?.sections || {};
    return Object.fromEntries(
      modeOptions.map((tab) => [tab.key, normalizeSection(rawSections[tab.key], tab.label)]),
    );
  }, [language, record]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeSection = sections[activeTab] || normalizeSection(null, 'Next moves');
  const status = record?.status || (hasRequestedPaths ? 'generating' : 'idle');
  const recordIntentMode = record?.intentMode === 'all' ? null : record?.intentMode;
  const recordSpecificRole = record?.specificRole || '';
  const recordMatchesPendingRequest = pendingRequest
    && (recordIntentMode || '') === (pendingRequest.mode || '')
    && recordSpecificRole === (pendingRequest.role || '')
    && (!pendingRequest.generationId || record?.generationId === pendingRequest.generationId);
  const waitingForSubmittedRecord = Boolean(pendingRequest && !recordMatchesPendingRequest);
  const isLoading = hasRequestedPaths && (
    ensuring
    || waitingForSubmittedRecord
    || status === 'generating'
    || (status !== 'ready' && activeSection.status === 'generating')
  );
  const displayPositions = (isLoading || error) ? [] : activeSection.positions;
  const hasPositions = displayPositions.length > 0;
  const showIntentPanel = recordLoaded && !hasRequestedPaths;
  const showResults = recordLoaded && hasRequestedPaths;
  const selectedPosition = displayPositions.find((position, index) => (
    selectedPositionKey === (position.title || `${activeTab}-${index}`)
  )) || null;

  const runEnsure = async ({ mode = intentMode, role = specificRole } = {}) => {
    if (!user?.uid || ensuring) return;
    const cleanRole = role.trim();
    setEnsuring(true);
    setPendingRequest({ mode, role: cleanRole });
    setHasRequestedPaths(true);
    setActiveTab(mode);
    setSelectedPositionKey('');
    setError('');
    try {
      const response = await ensureCareerPaths({ user, mode, specificRole: cleanRole });
      if (response?.status === 'ready') {
        setPendingRequest(null);
      } else if (response?.generationId) {
        setPendingRequest({ mode, role: cleanRole, generationId: response.generationId });
      }
    } catch (ensureError) {
      setPendingRequest(null);
      setError(ensureError.message || tc('startError'));
    } finally {
      setEnsuring(false);
    }
  };

  useEffect(() => {
    appliedRecordIntentRef.current = false;
    if (getCachedFirebaseValue(careerPathsPath) === undefined) setRecordLoaded(false);
    if (!user?.uid) {
      setRecordLoaded(true);
      return undefined;
    }
    const careerPathsRef = ref(db, careerPathsPath);
    const unsubscribe = subscribeCachedValue(careerPathsRef, careerPathsPath, (snapshot) => {
      const nextRecord = snapshot.exists() ? snapshot.val() : null;
      setRecord(nextRecord);
      setRecordLoaded(true);
      if (nextRecord?.status) {
        setHasRequestedPaths(true);
        const nextMode = nextRecord.intentMode === 'all' ? '' : nextRecord.intentMode;
        if (!appliedRecordIntentRef.current && TABS.some((tab) => tab.key === nextMode)) {
          appliedRecordIntentRef.current = true;
          setActiveTab(nextMode);
          setIntentMode(nextMode);
          setSpecificRole(nextRecord.specificRole || '');
        }
      }
      setPendingRequest((current) => {
        if (!current || !nextRecord) return current;
        const nextMode = nextRecord.intentMode === 'all' ? null : nextRecord.intentMode;
        const nextRole = nextRecord.specificRole || '';
        const sameRequest = (nextMode || '') === (current.mode || '')
          && nextRole === (current.role || '')
          && (!current.generationId || nextRecord.generationId === current.generationId);
        return sameRequest
          ? null
          : current;
      });
    });
    return unsubscribe;
  }, [user?.uid, careerPathsPath]);

  const handleIntentSubmit = (event) => {
    event.preventDefault();
    if (intentMode === 'pivoting' && !specificRole.trim()) return;
    runEnsure({ mode: intentMode, role: specificRole.trim() });
  };
  const retryMode = TABS.some((tab) => tab.key === record?.intentMode) ? record.intentMode : activeTab;
  const retryRole = record?.specificRole || specificRole;
  const pageTitle = selectedPosition ? selectedPosition.title : tc('nextMoves');
  const pageDescription = selectedPosition
    ? tc('roleDescription')
    : tc('description');
  const handleBack = () => {
    if (selectedPosition) {
      setSelectedPositionKey('');
      return;
    }
    navigate('/career-coach');
  };

  return (
    <section className="js-career-paths" aria-label={tc('label')}>
      <header className={`js-career-paths__head ${hasRequestedPaths ? '' : 'is-intent'}`}>
        <button type="button" className="js-mock-interview__back" onClick={handleBack}>
          <FiArrowLeft className="js-icon--directional" aria-hidden="true" />
          <span>{selectedPosition ? tc('nextMoves') : tc('back')}</span>
        </button>
        {hasRequestedPaths && (
          <div>
            <h1>{pageTitle}</h1>
            <p>{pageDescription}</p>
          </div>
        )}
        <PlanControlSlot />
      </header>

      {!recordLoaded && (
        <div className="js-career-paths__notice is-loading">
          <div className="js-personnel-spinner" />
          <div>
            <strong>{tc('loadingTitle')}</strong>
            <p>{tc('loadingDescription')}</p>
          </div>
        </div>
      )}

      {showIntentPanel && (
        <IntentPanel
          mode={intentMode}
          specificRole={specificRole}
          ensuring={ensuring}
          onModeChange={setIntentMode}
          onSpecificRoleChange={setSpecificRole}
          onSubmit={handleIntentSubmit}
          options={modeOptions}
          translate={tc}
        />
      )}

      {showResults && (
        <>
          {!selectedPosition && (
            <nav className="js-career-paths__tabs" aria-label={tc('categories')}>
              {modeOptions.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={activeTab === tab.key ? 'is-active' : ''}
                  onClick={() => {
                    setSelectedPositionKey('');
                    setActiveTab(tab.key);
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          )}

          {isLoading && (
            <div className="js-career-paths__notice is-loading">
              <div className="js-personnel-spinner" />
              <div>
                <strong>{tc('buildingTitle')}</strong>
                <p>{tc('buildingDescription')}</p>
              </div>
            </div>
          )}

          {(error || (status === 'error' && !hasPositions)) && (
            <div className="js-career-paths__notice is-error">
              <div>
                <strong>{tc('errorTitle')}</strong>
                <p>{error || record?.error || activeSection.error || tc('errorFallback')}</p>
              </div>
              <button type="button" onClick={() => runEnsure({ mode: retryMode, role: retryRole })} disabled={ensuring}>
                <FiRefreshCw aria-hidden="true" />
                <span>{tc('retry')}</span>
              </button>
            </div>
          )}

          {selectedPosition ? (
            <PositionDetail position={selectedPosition} language={language} translate={tc} skillsAction={
              <button type="button" className="js-career-skills-next" dir={language === 'ar' ? 'rtl' : 'ltr'} onClick={() => navigate('/learning-pathways', { state: { targetRole: selectedPosition.title } })}>
                <FiZap className="js-career-skills-next__icon" aria-hidden="true" />
                <span>{language === 'ar' ? 'طوّر هذه المهارات من خلال مسار تعلّم مخصص لك.' : 'Build these skills with a personalized learning pathway.'}</span>
                <FiArrowRight className="js-career-skills-next__arrow js-icon--directional" aria-hidden="true" />
              </button>
            } />
          ) : (
            <>
              <div className="js-career-paths__section-head">
                <div>
                  <h2>{modeOptions.find((tab) => tab.key === activeTab)?.label || activeSection.title}</h2>
                  {activeSection.status === 'error' && activeSection.error && <p>{activeSection.error}</p>}
                </div>
              </div>

              <div className="js-career-paths__list">
                {displayPositions.map((position, index) => {
                  const key = position.title || `${activeTab}-${index}`;
                  return (
                    <PositionCard
                      key={key}
                      position={position}
                      onOpen={() => setSelectedPositionKey(key)}
                      translate={tc}
                    />
                  );
                })}
                {!isLoading && !hasPositions && status !== 'error' && (
                  <div className="js-career-paths__empty">{tc('empty')}</div>
                )}
              </div>

              <SourceStrip sources={activeSection.sources} translate={tc} />
            </>
          )}
        </>
      )}
    </section>
  );
}

export default CareerPathsScreen;
